<template>
    <b-container fluid>
        <b-row>
            <b-col cols="12">
                <div class="border-bottom mb-3 pb-3 d-flex justify-content-between align-items-center">
                    <div>
                        <h3>이메일 템플릿 관리</h3>
                        <p class="fw-400 text-secondary mb-0">발송 이메일의 템플릿을 관리합니다.</p>
                    </div>
                    <div>
                        <b-button variant="primary" @click="goToNewEditor">
                            + 새 템플릿
                        </b-button>
                    </div>
                </div>
            </b-col>
        </b-row>

        <!-- 검색 -->
        <b-row class="mb-3"><b-col cols="12">
        <b-card no-body bg-variant="white" class="p-3">
            <b-row>
                <b-col cols="12" md="3" class="mb-2 mb-md-0">
                    <b-form-input
                        v-model="searchForm.keyword"
                        placeholder="템플릿 이름 검색"
                        size="sm"
                        @keyup.enter="searchData"
                        :disabled="loading"
                    />
                </b-col>
                <b-col cols="6" md="3" class="mb-2 mb-md-0">
                    <b-form-select
                        v-model="searchForm.scenario"
                        :options="scenarioOptions"
                        size="sm"
                        :disabled="loading"
                    />
                </b-col>
                <b-col cols="6" md="3" class="mb-2 mb-md-0">
                    <b-form-select
                        v-model="searchForm.subDomain"
                        :options="subDomainOptions"
                        size="sm"
                        :disabled="loading"
                    />
                </b-col>
                <b-col cols="12" md="3" class="text-md-right">
                    <b-button variant="primary" size="sm" @click="searchData" :disabled="loading" class="mr-1">검색</b-button>
                    <b-button variant="outline-secondary" size="sm" @click="resetSearch" :disabled="loading">초기화</b-button>
                </b-col>
            </b-row>
        </b-card>
        </b-col></b-row>

        <!-- 목록 -->
        <b-row><b-col cols="12">
        <b-card no-body bg-variant="white" class="p-3">
            <b-table
                :items="templates"
                :fields="columns"
                responsive
                hover
                striped
                show-empty
                :busy="loading"
                empty-text="등록된 템플릿이 없습니다."
            >
                <template #table-busy>
                    <div class="text-center my-3">
                        <b-spinner variant="primary" />
                    </div>
                </template>

                <template #cell(scenario_code)="{ item }">
                    <span :title="item.scenario_code">{{ scenarioLabel(item.scenario_code) }}</span>
                </template>

                <template #cell(label)="{ item }">
                    {{ item.label }}
                    <b-badge v-if="item.is_fallback" variant="info" class="ml-1">기본</b-badge>
                </template>

                <template #cell(contexts)="{ item }">
                    <span v-if="item.is_fallback" class="text-muted">없음 (기본)</span>
                    <div v-else class="condition-cell">
                        <div v-for="(v, k) in item.contexts" :key="k" :title="`${contextKeyLabel(k)}: ${v}`" class="condition-row">
                            <span class="condition-key">{{ contextKeyLabel(k) }}</span>
                            <span
                                v-for="(part, i) in conditionValueParts(k, v)"
                                :key="`${k}-${i}`"
                                :title="part.id ? `#${part.id}` : part.text"
                                class="condition-chip"
                            >{{ part.text }}</span>
                        </div>
                    </div>
                </template>

                <template #cell(locales)="{ item }">
                    <b-badge
                        v-for="locale in (item.locales || [])"
                        :key="locale"
                        variant="outline-secondary"
                        class="mr-1"
                    >{{ locale }}</b-badge>
                </template>

                <template #cell(updated_at)="{ item }">
                    {{ formatDate(item.updated_at) }}
                </template>

                <template #cell(action)="{ item }">
                    <b-button
                        variant="outline-primary"
                        size="sm"
                        class="mr-1"
                        @click="goToEditor(item.id)"
                    >편집</b-button>
                    <b-button
                        variant="outline-secondary"
                        size="sm"
                        title="모든 언어·발송 상황·조건을 채운 새 템플릿 화면을 엽니다. '생성'을 누르기 전에는 만들어지지 않습니다."
                        @click="goToDuplicate(item.id)"
                    >복제</b-button>
                </template>
            </b-table>

            <div class="d-flex justify-content-center mt-3" v-if="pagination.totalRows > pagination.perPage">
                <b-pagination
                    v-model="pagination.currentPage"
                    :total-rows="pagination.totalRows"
                    :per-page="pagination.perPage"
                    @change="onPageChange"
                />
            </div>
        </b-card>
        </b-col></b-row>
    </b-container>
</template>

<script>
import { dashboardColumns, CONTEXT_KEYS, sortScenarios } from './MailEditor/data'
// ⛔ CommonJS 헬퍼는 이 코드베이스 관례대로 default import 로 받는다.
import conditionLabels from './_common/conditionLabels'

export default {
    name: 'MailDashboard',
    data() {
        return {
            loading: false,
            templates: [],
            columns: dashboardColumns,
            searchForm: {
                keyword: '',
                scenario: '',
                subDomain: '',
            },
            pagination: {
                currentPage: 1,
                perPage: 20,
                totalRows: 0,
            },
            scenarios: [],
            subDomains: [],
            // labelKey → { label, sub } | null(찾을 수 없음). 페이지를 넘겨도 유지해 같은 ID 를 다시 묻지 않는다.
            conditionNames: {},
            // 권한 없음(403)이면 더 묻지 않고 원문 그대로 보여 준다. 일시적 실패는 다음 조회 때 다시 시도.
            conditionLookupForbidden: false,
        }
    },
    computed: {
        scenarioOptions() {
            const opts = [{ value: '', text: '전체 시나리오' }]
            this.scenarios.forEach(s => {
                opts.push({ value: s.code, text: s.label || s.code })
            })
            return opts
        },
        subDomainOptions() {
            const opts = [{ value: '', text: '전체 서브도메인' }]
            this.subDomains.forEach(sd => {
                opts.push({ value: sd, text: sd })
            })
            return opts
        },
        currentQuery() {
            const q = {}
            if (this.searchForm.keyword) q.keyword = this.searchForm.keyword
            if (this.searchForm.scenario) q.scenario = this.searchForm.scenario
            if (this.searchForm.subDomain) q.sub_domain = this.searchForm.subDomain
            if (this.pagination.currentPage > 1) q.page = String(this.pagination.currentPage)
            return q
        },
    },
    mounted() {
        this.initFromRouteQuery()
        this.loadScenarios()
        this.loadSubDomains()
        this.fetchData()
    },
    methods: {
        initFromRouteQuery() {
            const q = this.$route.query || {}
            this.searchForm.keyword = q.keyword || ''
            this.searchForm.scenario = q.scenario || ''
            this.searchForm.subDomain = q.sub_domain || ''
            const p = parseInt(q.page, 10)
            this.pagination.currentPage = Number.isFinite(p) && p >= 1 ? p : 1
        },
        syncRouteQuery() {
            const current = this.$route.query || {}
            const next = this.currentQuery
            // 동일하면 replace 스킵 (Avoid Redundant Navigation)
            const same = Object.keys(next).length === Object.keys(current).length
                && Object.keys(next).every(k => String(current[k]) === String(next[k]))
            if (same) return
            this.$router.replace({ query: next }).catch(err => {
                if (err && err.name !== 'NavigationDuplicated') throw err
            })
        },
        async loadScenarios() {
            try {
                const res = await this.$axios.get('/mail/scenario')
                this.scenarios = sortScenarios(res.data || [])
            } catch (e) {
                console.error(e)
                this.$EventBus.$emit('toast-failure', '조회 오류', '발송 상황 목록을 불러오지 못했습니다. 새로고침 후 다시 시도해주세요.')
            }
        },
        async loadSubDomains() {
            // 조건(context)에 실제로 쓰인 sub_domain 값을 데이터에서 추출해 드롭다운 구성 (하드코딩 X)
            try {
                const res = await this.$axios.get('/mail/template', { params: { page: 1, size: 500 } })
                const set = new Set()
                ;(res.data.results || []).forEach(t => {
                    const sd = t.contexts && t.contexts.sub_domain
                    if (sd) set.add(sd)
                })
                this.subDomains = [...set].sort((a, b) => String(a).localeCompare(String(b)))
            } catch (e) {
                console.error(e)
                this.$EventBus.$emit('toast-failure', '조회 오류', '서브도메인 목록을 불러오지 못했습니다. 새로고침 후 다시 시도해주세요.')
            }
        },
        async fetchData() {
            this.loading = true
            try {
                const params = {
                    page: this.pagination.currentPage,
                    size: this.pagination.perPage,
                }
                if (this.searchForm.keyword) params.keyword = this.searchForm.keyword
                if (this.searchForm.scenario) params.scenario = this.searchForm.scenario
                if (this.searchForm.subDomain) params.context = 'sub_domain:' + this.searchForm.subDomain

                const res = await this.$axios.get('/mail/template', { params })
                const data = res.data || {}
                this.templates = data.results || []
                this.pagination.totalRows = data.total || 0
                this.resolveConditionLabels()
            } catch (e) {
                console.error(e)
                this.$EventBus.$emit('toast-failure', '오류', '템플릿 목록을 불러올 수 없습니다.')
            } finally {
                this.loading = false
            }
        },
        searchData() {
            this.pagination.currentPage = 1
            this.syncRouteQuery()
            this.fetchData()
        },
        resetSearch() {
            this.searchForm.keyword = ''
            this.searchForm.scenario = ''
            this.searchForm.subDomain = ''
            this.pagination.currentPage = 1
            this.syncRouteQuery()
            this.fetchData()
        },
        onPageChange(page) {
            this.pagination.currentPage = page
            this.syncRouteQuery()
            this.fetchData()
        },
        formatDate(dt) {
            if (!dt) return ''
            return new Date(dt).toLocaleDateString('ko-KR', {
                year: 'numeric', month: '2-digit', day: '2-digit',
            })
        },
        contextKeyLabel(k) {
            const found = CONTEXT_KEYS.find(c => c.value === k)
            return found ? found.label : k
        },
        entityOf(k) {
            const found = CONTEXT_KEYS.find(c => c.value === k)
            return found ? found.entity : ''
        },
        conditionValueParts(k, v) {
            return conditionLabels.conditionValueParts(this.entityOf(k), v, this.conditionNames)
        },
        // 화면에 있는 행들의 ID 를 엔티티별 한 번씩만 물어 이름으로 바꾼다. 실패하면 원문 표시 그대로 둔다.
        async resolveConditionLabels() {
            if (this.conditionLookupForbidden) return
            const requests = conditionLabels.collectLookupRequests(this.templates, this.entityOf, this.conditionNames)
            if (!requests.length) return
            await Promise.all(requests.map(async ({ entity, ids }) => {
                try {
                    const { data } = await this.$axios.get(`/mail/lookup/${entity}`, { params: { ids: ids.join(',') } })
                    const found = {}
                    ;((data && data.items) || []).forEach(item => {
                        found[item.value] = { label: item.label, sub: item.sub }
                    })
                    // 응답이 오는 대로 병합한다. 스냅샷을 통째로 덮으면 페이지를 빨리 넘길 때 늦게 온 응답이 먼저 온 이름을 지운다.
                    const delta = {}
                    ids.forEach(id => {
                        delta[conditionLabels.labelKey(entity, id)] = found[id] || null
                    })
                    this.conditionNames = { ...this.conditionNames, ...delta }
                } catch (e) {
                    if (e && e.response && e.response.status === 403) this.conditionLookupForbidden = true
                }
            }))
        },
        scenarioLabel(code) {
            const found = this.scenarios.find(s => s.code === code)
            return found ? (found.label || code) : code
        },
        goToNewEditor() {
            this.$router.push({ name: 'MailEditorNew', query: this.currentQuery })
        },
        goToEditor(id) {
            this.$router.push({ name: 'MailEditorEdit', params: { id }, query: this.currentQuery })
        },
        goToDuplicate(id) {
            this.$router.push({ name: 'MailEditorNew', query: { ...this.currentQuery, copy_from: String(id) } })
        },
    },
}
</script>

<style scoped>
/* 조건 열: 키 한 줄에 값 칩 나열. 배지(가운데 정렬·한 줄)로는 이름이 길어지면 읽기 어렵다. */
.condition-cell {
    text-align: left;
}
.condition-row + .condition-row {
    margin-top: 4px;
}
.condition-key {
    display: inline-block;
    min-width: 76px;
    margin-right: 6px;
    color: #6c757d;
    font-size: 12px;
}
.condition-chip {
    display: inline-block;
    margin: 0 4px 2px 0;
    padding: 1px 6px;
    font-size: 12px;
    line-height: 1.5;
    background-color: #f1f3f5;
    border: 1px solid #e3e6ea;
    border-radius: 3px;
}
</style>
